import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api, { mediaUrl } from "@/lib/api";
import { useStoreBase } from "@/lib/store";
import { ArrowRight } from "lucide-react";
import { formatBlogDate } from "@/lib/storeBlog";

export default function StorefrontBlogIndex({ config }) {
  const { slug, base } = useStoreBase();
  const [params, setParams] = useSearchParams();
  const cat = params.get("category") || "";
  const [posts, setPosts] = useState(null);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    if (!config?.store_blog_active || !slug) return;
    const q = cat ? `?category=${encodeURIComponent(cat)}` : "";
    api.get(`/store/${slug}/blogs${q}`).then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  }, [slug, cat, config?.store_blog_active]);

  useEffect(() => {
    if (!config?.store_blog_active || !slug) return;
    api.get(`/store/${slug}/blogs/categories`).then(({ data }) => setCats(data)).catch(() => setCats([]));
  }, [slug, config?.store_blog_active]);

  if (!config?.store_blog_active) return <Navigate to={`${base}/`} replace />;
  const storeName = config.store_info?.name || "Store";
  const heading = config.store_blog?.heading || "Latest Stories";

  return (
    <div className="bg-[#F6F7F9] min-h-[70vh]" data-testid="store-blog-index">
      <Helmet>
        <title>{heading} — {storeName}</title>
        <meta name="description" content={`Stories and guides from ${storeName}.`} />
      </Helmet>
      <section className="relative overflow-hidden bg-stone-900 text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-700 via-indigo-800 to-stone-900 opacity-90" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-200">Journal</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold">{heading}</h1>
          <p className="mt-3 text-white/80 max-w-xl mx-auto">Guides, routines, and stories from {storeName}.</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        {cats.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center mb-10" data-testid="store-blog-cats">
            <button type="button" onClick={() => setParams({})} className={`px-4 py-1.5 rounded-full text-sm border ${!cat ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-600 border-stone-200"}`}>All</button>
            {cats.map((c) => (
              <button key={c.id} type="button" onClick={() => setParams({ category: c.slug })} className={`px-4 py-1.5 rounded-full text-sm border ${cat === c.slug ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-600 border-stone-200"}`} data-testid={`store-blog-cat-${c.slug}`}>{c.name}</button>
            ))}
          </div>
        )}

        {posts == null ? (
          <div className="text-center text-stone-500 py-16">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="text-center text-stone-500 py-16" data-testid="store-blog-empty">No stories yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link key={p.id} to={`${base}/blog/${p.slug}`} className="bg-white rounded-[22px] overflow-hidden border border-stone-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`store-blog-card-${p.slug}`}>
                <div className="h-48 bg-stone-100">
                  {p.featured_image_url ? <img src={mediaUrl(p.featured_image_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-sky-100 to-indigo-100" />}
                </div>
                <div className="p-5">
                  <div className="text-xs font-medium text-sky-600">{formatBlogDate(p.published_at)}{p.category_name ? ` · ${p.category_name}` : ""}</div>
                  <h2 className="mt-2 font-display font-bold text-lg text-stone-900 line-clamp-2">{p.title}</h2>
                  <p className="mt-2 text-sm text-stone-500 line-clamp-3">{p.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-sky-600">Read more <ArrowRight className="w-3.5 h-3.5" /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
